import { useState, useEffect } from 'react'
import { 
  MessageSquare, Send, RefreshCw, Trash2, CheckCircle, 
  XCircle, Clock, AlertCircle, Wifi, WifiOff 
} from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  sent: { label: 'Envoyé', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  failed: { label: 'Échec', color: 'bg-red-100 text-red-700', icon: XCircle },
}

const TYPE_LABELS = {
  confirmation: 'Confirmation',
  reminder_48h: 'Rappel 48h',
  reminder_24h: 'Rappel 24h',
  cancellation: 'Annulation',
  campaign: 'Campagne'
}

export default function SmsPage() {
  const [queue, setQueue] = useState([])
  const [gatewayStatus, setGatewayStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const[testPhone, setTestPhone] = useState('')
  const [testMessage, setTestMessage] = useState('')
  const [sending, setSending] = useState(false)

  const fetchData = async () => {
    try {
      const [queueRes, statusRes] = await Promise.all([
        api.get('/api/sms/queue'),
        api.get('/api/sms/gateway/status')
      ])
      setQueue(queueRes.data)
      setGatewayStatus(statusRes.data)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  },[])

  const handleProcessQueue = async () => {
    try {
      const res = await api.post('/api/sms/process-queue')
      toast.success(`${res.data.sent} SMS envoyé(s), ${res.data.failed} échec(s)`)
      fetchData()
    } catch (error) {
      toast.error('Erreur lors du traitement')
    }
  }

  const handleRetry = async (id) => {
    try {
      await api.post(`/api/sms/retry/${id}`)
      toast.success('SMS remis en queue')
      fetchData()
    } catch (error) {
      toast.error('Erreur')
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce SMS ?')) return
    try {
      await api.delete(`/api/sms/${id}`)
      toast.success('SMS supprimé')
      fetchData()
    } catch (error) {
      toast.error('Erreur')
    }
  }

  const handleTestSms = async (e) => {
    e.preventDefault()
    if (!testPhone || !testMessage) {
      toast.error('Remplissez tous les champs')
      return
    }
    setSending(true)
    try {
      await api.post('/api/sms/test', { phone: testPhone, message: testMessage })
      toast.success('SMS envoyé !')
      setTestPhone('')
      setTestMessage('')
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'envoi")
    } finally {
      setSending(false)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    })
  }

  const filteredQueue = queue.filter(sms => {
    if (filter === 'all') return true
    return sms.status === filter
  })

  const pendingCount = queue.filter(s => s.status === 'pending').length
  const failedCount = queue.filter(s => s.status === 'failed').length

  if (loading) return <div className="flex justify-center items-center h-64"><div className="spinner border-sage-600" /></div>

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif text-gray-800">SMS</h1>
          <p className="text-gray-500 mt-1">Gestion des SMS et de la passerelle</p>
        </div>
        <button onClick={fetchData} className="btn btn-secondary flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Actualiser
        </button>
      </div>

      {/* Gateway status (Modifié en 2 colonnes avec min-w-0 pour éviter le débordement) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={`card ${gatewayStatus?.available ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
            <div className="flex items-center gap-4">
              {gatewayStatus?.available ? (
                <div className="p-3 bg-green-100 rounded-xl"><Wifi className="w-6 h-6 text-green-600" /></div>
              ) : (
                <div className="p-3 bg-red-100 rounded-xl"><WifiOff className="w-6 h-6 text-red-600" /></div>
              )}
              <div className="flex-1 min-w-0">
                <p className={`font-bold ${gatewayStatus?.available ? 'text-green-700' : 'text-red-700'}`}>
                  {gatewayStatus?.available ? 'Connexion Passerelle SMS OK' : 'Passerelle SMS injoignable'}
                </p>
                <p className="text-xs text-gray-500 truncate mt-0.5" title={gatewayStatus?.url}>{gatewayStatus?.url}</p>
                {gatewayStatus?.error && <p className="text-xs text-red-600 mt-1 truncate" title={gatewayStatus.error}>{gatewayStatus.error}</p>}
              </div>
            </div>
          </div>

          <div className={`card ${gatewayStatus?.is_enabled ? 'border-blue-200 bg-blue-50' : 'border-orange-200 bg-orange-50'}`}>
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${gatewayStatus?.is_enabled ? 'bg-blue-100' : 'bg-orange-100'}`}>
                <MessageSquare className={`w-6 h-6 ${gatewayStatus?.is_enabled ? 'text-blue-600' : 'text-orange-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-bold truncate ${gatewayStatus?.is_enabled ? 'text-blue-700' : 'text-orange-700'}`}>
                  {gatewayStatus?.is_enabled ? 'Envois Automatiques ACTIVÉS' : 'Envois Automatiques DÉSACTIVÉS'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {gatewayStatus?.is_enabled ? 'Les rappels de RDV partent normalement.' : 'Seuls vos envois manuels partiront.'}
                </p>
              </div>
            </div>
          </div>
      </div>

      {/* Stats & actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">En attente</p>
              <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-200" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">En échec</p>
              <p className="text-2xl font-bold text-red-600">{failedCount}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-200" />
          </div>
        </div>
        <div className="card flex items-center justify-center p-4">
          <button
            onClick={handleProcessQueue}
            disabled={pendingCount === 0}
            className="btn btn-primary w-full h-full flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" />
            Envoyer la file d'attente
          </button>
        </div>
      </div>

      {/* Test SMS */}
      <div className="card">
        <h3 className="font-medium text-gray-800 mb-4">Envoyer un SMS manuel</h3>
        <form onSubmit={handleTestSms} className="flex flex-col sm:flex-row gap-4">
          <input
            type="tel"
            placeholder="Numéro (ex: +336...)"
            value={testPhone}
            onChange={e => setTestPhone(e.target.value)}
            className="sm:w-1/3"
          />
          <input
            type="text"
            placeholder="Message..."
            value={testMessage}
            onChange={e => setTestMessage(e.target.value)}
            className="flex-1"
          />
          <button
            type="submit"
            disabled={sending}
            className="btn btn-primary flex items-center justify-center gap-2"
          >
            {sending ? <div className="spinner w-4 h-4 border-white" /> : <Send className="w-4 h-4" />}
            Envoyer
          </button>
        </form>
      </div>

      {/* Queue filter */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {[
          { value: 'all', label: 'Tous' },
          { value: 'pending', label: 'En attente' },
          { value: 'sent', label: 'Envoyés' },
          { value: 'failed', label: 'Échecs' },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              filter === tab.value
                ? 'bg-sage-100 text-sage-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Queue list */}
      {filteredQueue.length === 0 ? (
        <div className="card text-center py-12">
          <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Aucun SMS dans la file</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredQueue.map(sms => {
            const status = STATUS_CONFIG[sms.status] || STATUS_CONFIG.pending
            return (
              <div key={sms.id} className="card p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${status.color}`}>
                        <status.icon className="w-3 h-3" />
                        {status.label}
                      </span>
                      <span className="text-[10px] text-gray-500 bg-gray-100 border border-gray-200 px-2 py-1 rounded font-bold uppercase">
                        {TYPE_LABELS[sms.sms_type] || sms.sms_type}
                      </span>
                    </div>
                    <p className="font-bold text-gray-800">{sms.phone}</p>
                    <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap leading-snug">{sms.message}</p>
                    {sms.error_message && (
                      <p className="text-sm font-medium text-red-600 bg-red-50 p-2 rounded mt-2 border border-red-100">❌ Erreur : {sms.error_message}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-3 flex items-center gap-2">
                      <Clock className="w-3 h-3"/> Prévu le {formatDate(sms.scheduled_at)}
                      <span className="text-gray-300">|</span>
                      Tentatives : {sms.attempts}/3
                      {sms.sent_at && <><span className="text-gray-300">|</span> Envoyé le {formatDate(sms.sent_at)}</>}
                    </p>
                  </div>
                  <div className="flex lg:flex-col gap-2 justify-end">
                    {sms.status === 'failed' && (
                      <button onClick={() => handleRetry(sms.id)} className="btn btn-secondary flex items-center justify-center gap-2 text-sm w-full lg:w-auto">
                        <RefreshCw className="w-4 h-4" /> Réessayer
                      </button>
                    )}
                    <button onClick={() => handleDelete(sms.id)} className="btn bg-white border border-gray-200 text-gray-400 hover:text-red-500 hover:bg-red-50 hover:border-red-200 flex items-center justify-center w-full lg:w-auto p-2 transition-all">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}